import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Shield, CheckCircle, AlertTriangle, Loader2, User, Lock, Eye, EyeOff, Copy } from 'lucide-react';
import api from '../services/api';

const PaymentLink = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showIptvPassword, setShowIptvPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [countdown, setCountdown] = useState(5);
  
  const [formData, setFormData] = useState({
    password: '',
    login_password: '',
    device_type: '',
    iptv_app: '',
    mac_address: '',
    device_id: '',
    snapchat: ''
  });

  useEffect(() => {
    fetchLinkData();
    
    // Gérer le retour Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const stripeSessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'stripe_success' && stripeSessionId) {
      const savedFormData = sessionStorage.getItem('payment_link_form_data');
      const savedToken = sessionStorage.getItem('payment_link_token');
      
      if (savedToken) {
        handleStripeReturn(savedToken, stripeSessionId);
      }
    } else if (paymentStatus === 'cancelled') {
      setError('Paiement annulé');
      sessionStorage.removeItem('payment_link_form_data');
      sessionStorage.removeItem('payment_link_token');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  const handleStripeReturn = async (linkToken, sessionId) => {
    setProcessing(true);
    
    try {
      // Compléter le paiement Stripe
      const response = await api.post(`/payment-links/${linkToken}/complete-stripe`, {
        session_id: sessionId,
        host_url: `${window.location.origin}/`
      });
      
      if (response.data.status === 'completed') {
        setResult(response.data);
        setSuccess(true);
      } else {
        // Si pas encore confirmé, faire du polling
        const poll = async (attempts = 0) => {
          if (attempts >= 8) {
            setError('Vérification en cours. Votre accès sera activé automatiquement.');
            return;
          }
          try {
            const res = await api.post(`/payment-links/${linkToken}/complete-stripe`, {
              session_id: sessionId,
              host_url: `${window.location.origin}/`
            });
            if (res.data.status === 'completed') {
              setResult(res.data);
              setSuccess(true);
              return;
            }
          } catch (e) { /* continue */ }
          setTimeout(() => poll(attempts + 1), 2500);
        };
        poll();
      }
      
      sessionStorage.removeItem('payment_link_form_data');
      sessionStorage.removeItem('payment_link_token');
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la validation du paiement');
    } finally {
      setProcessing(false);
    }
  };

  const fetchLinkData = async () => {
    try {
      const response = await api.get(`/payment-links/${token}`);
      setLinkData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien de paiement invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError(null);

    try {
      const payload = {
        ...formData
      };
      
      if (linkData?.existing_user) {
        payload.login_password = formData.login_password;
      } else {
        payload.password = formData.password;
      }
      
      // Créer un devis signé et rediriger vers Site B (Stripe)
      const response = await api.post(`/payment-links/${token}/create-checkout`, payload);
      
      // Rediriger vers Stripe Checkout
      if (response.data.checkout_url) {
        sessionStorage.setItem('payment_link_form_data', JSON.stringify(payload));
        sessionStorage.setItem('payment_link_token', token);
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de paiement non disponible');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du paiement');
      setProcessing(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Countdown et redirection automatique après succès
  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      // Rediriger vers la page de connexion ou dashboard
      navigate('/connexion');
    }
  }, [success, countdown, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  if (error && !linkData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full text-center"
        >
          <AlertTriangle className="h-16 w-16 text-[#FF3B5C] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Lien invalide</h1>
          <p className="text-[#B8B8BA] mb-6">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="btn-primary"
          >
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    );
  }

  if (success && result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-lg w-full"
        >
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-[#00FF88]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-[#00FF88]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Paiement réussi !</h1>
            <p className="text-[#B8B8BA] mb-4">
              {result.is_new_user 
                ? "Votre compte a été créé. Vérifiez votre email pour activer votre compte."
                : "Votre nouvel abonnement a été ajouté à votre compte."}
            </p>
            
            <div className="bg-[#222226] p-3 rounded-lg">
              <p className="text-sm text-[#B8B8BA]">
                Redirection automatique dans <span className="text-[#00FFFF] font-bold">{countdown}s</span>
              </p>
            </div>
          </div>

          {/* IPTV Credentials */}
          {result.iptv_credentials && (
            <div className="bg-[#222226] p-6 rounded-lg border border-[#00FFFF]/30 mb-6">
              <h3 className="text-lg font-bold text-[#00FFFF] mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Vos identifiants IPTV
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-[#1A1A1D] p-3 rounded">
                  <div>
                    <div className="text-[#6B6B6E] text-xs">Utilisateur</div>
                    <div className="text-white font-mono">{result.iptv_credentials.username}</div>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.iptv_credentials.username, 'username')}
                    className="text-[#00FFFF] hover:text-white p-2"
                  >
                    {copiedField === 'username' ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between bg-[#1A1A1D] p-3 rounded">
                  <div>
                    <div className="text-[#6B6B6E] text-xs">Mot de passe</div>
                    <div className="text-white font-mono flex items-center">
                      {showIptvPassword ? result.iptv_credentials.password : '••••••••••••'}
                      <button 
                        onClick={() => setShowIptvPassword(!showIptvPassword)}
                        className="ml-2 text-[#6B6B6E] hover:text-white"
                      >
                        {showIptvPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.iptv_credentials.password, 'password')}
                    className="text-[#00FFFF] hover:text-white p-2"
                  >
                    {copiedField === 'password' ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between bg-[#1A1A1D] p-3 rounded">
                  <div>
                    <div className="text-[#6B6B6E] text-xs">Serveur</div>
                    <div className="text-white font-mono">{result.iptv_credentials.server_url}{result.iptv_credentials.port ? `:${result.iptv_credentials.port}` : ''}</div>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(`${result.iptv_credentials.server_url}${result.iptv_credentials.port ? `:${result.iptv_credentials.port}` : ''}`, 'server')}
                    className="text-[#00FFFF] hover:text-white p-2"
                  >
                    {copiedField === 'server' ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <p className="text-[#FFB800] text-sm mt-4">
                📧 Ces identifiants ont également été envoyés par email.
              </p>
            </div>
          )}

          <div className="text-center space-y-3">
            <button 
              onClick={() => navigate('/connexion')} 
              className="btn-primary w-full"
            >
              Se connecter à mon compte
            </button>
            <button 
              onClick={() => navigate('/tutoriels')} 
              className="btn-secondary w-full"
            >
              Voir les tutoriels d'installation
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00FFFF]/20 to-[#0099FF]/20 p-6 border-b border-[#3A3A3D]">
            <h1 className="text-2xl font-bold text-white mb-2">Finaliser votre paiement</h1>
            <p className="text-[#B8B8BA]">
              {linkData?.existing_user 
                ? `Bienvenue ${linkData?.existing_user_name || linkData?.full_name} ! Connectez-vous pour ajouter un nouvel accès.`
                : `Bienvenue ${linkData?.full_name} ! Créez votre compte pour commencer.`
              }
            </p>
          </div>

          {/* Order Summary */}
          <div className="p-6 border-b border-[#3A3A3D]">
            <h2 className="text-lg font-semibold text-white mb-4">Récapitulatif</h2>
            <div className="bg-[#222226] rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-[#B8B8BA]">Offre</span>
                <span className="text-white font-medium">{linkData?.plan_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#B8B8BA]">Email</span>
                <span className="text-white">{linkData?.email}</span>
              </div>
              {linkData?.existing_user && (
                <div className="flex items-center space-x-2 text-[#00FF88] text-sm">
                  <User className="h-4 w-4" />
                  <span>{linkData?.is_renewal ? 'Renouvellement de votre abonnement' : 'Compte existant détecté - Nouvel accès IPTV'}</span>
                </div>
              )}
              
              {/* Renewal notice - no referral discount */}
              {linkData?.is_renewal && linkData?.discount === 0 && (
                <div className="text-[#6B6B6E] text-xs">
                  * Les réductions de parrainage ne sont pas applicables aux renouvellements
                </div>
              )}
              
              {/* Referral Discount */}
              {linkData?.discount > 0 && (
                <>
                  <div className="flex justify-between text-[#B8B8BA]">
                    <span>Prix initial</span>
                    <span className="line-through">{linkData?.amount}€</span>
                  </div>
                  <div className="flex justify-between text-[#00FF88]">
                    <span className="flex items-center space-x-1">
                      <span>🎁</span>
                      <span>Réduction parrainage{linkData?.referrer_name ? ` (via ${linkData.referrer_name})` : ''}</span>
                    </span>
                    <span>-{linkData?.discount}€</span>
                  </div>
                </>
              )}
              
              <div className="border-t border-[#3A3A3D] pt-3 flex justify-between">
                <span className="text-white font-semibold">Total à payer</span>
                <span className="text-[#00FFFF] text-2xl font-bold">
                  {linkData?.final_amount !== undefined ? linkData.final_amount : linkData?.amount}€
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {linkData?.existing_user ? (
              /* Existing User - Login Form */
              <div className="bg-[#222226] p-6 rounded-lg border border-[#00FFFF]/30">
                <div className="flex items-center space-x-2 mb-4">
                  <User className="h-5 w-5 text-[#00FFFF]" />
                  <h3 className="text-white font-semibold">Connexion requise</h3>
                </div>
                <p className="text-[#B8B8BA] text-sm mb-4">
                  Un compte existe déjà avec cet email. Entrez votre mot de passe pour ajouter un nouvel accès IPTV.
                </p>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.login_password}
                    onChange={(e) => setFormData({...formData, login_password: e.target.value})}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none pr-12"
                    placeholder="Votre mot de passe"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6B6E] hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <a href="/mot-de-passe-oublie" className="text-[#00FFFF] text-sm mt-2 inline-block hover:underline">
                  Mot de passe oublié ?
                </a>
              </div>
            ) : (
              /* New User - Registration Form */
              <>
                <div className="bg-[#222226] p-6 rounded-lg border border-[#00FF88]/30">
                  <div className="flex items-center space-x-2 mb-4">
                    <Lock className="h-5 w-5 text-[#00FF88]" />
                    <h3 className="text-white font-semibold">Création de compte</h3>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FF88] focus:outline-none pr-12"
                      placeholder="Créez un mot de passe (min. 8 caractères)"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6B6E] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-[#6B6B6E] text-xs mt-2">
                    Un email de vérification vous sera envoyé après le paiement.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#E8E8E8] mb-2">Type d'appareil</label>
                    <select
                      value={formData.device_type}
                      onChange={(e) => setFormData({...formData, device_type: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="smart_tv">Smart TV</option>
                      <option value="android_box">Box Android</option>
                      <option value="firestick">Fire Stick</option>
                      <option value="smartphone">Smartphone</option>
                      <option value="tablet">Tablette</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#E8E8E8] mb-2">Application IPTV</label>
                    <input
                      type="text"
                      value={formData.iptv_app}
                      onChange={(e) => setFormData({...formData, iptv_app: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                      placeholder="IPTV Smarters, TiviMate..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#E8E8E8] mb-2">Adresse MAC <span className="text-[#6B6B6E] text-xs">(recommandé)</span></label>
                    <input
                      type="text"
                      value={formData.mac_address}
                      onChange={(e) => setFormData({...formData, mac_address: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                      placeholder="AA:BB:CC:DD:EE:FF"
                    />
                  </div>
                  <div>
                    <label className="block text-[#E8E8E8] mb-2">Device ID <span className="text-[#6B6B6E] text-xs">(recommandé)</span></label>
                    <input
                      type="text"
                      value={formData.device_id}
                      onChange={(e) => setFormData({...formData, device_id: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                      placeholder="Identifiant de l'appareil"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Security Badge */}
            <div className="flex items-center space-x-3 bg-[#222226] p-4 rounded-lg">
              <Shield className="h-8 w-8 text-[#00FF88]" />
              <div>
                <p className="text-white font-medium">Paiement 100% sécurisé</p>
                <p className="text-[#6B6B6E] text-sm">Vos informations sont protégées</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={processing}
              className="btn-primary w-full py-4 text-lg flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {processing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Traitement en cours...</span>
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  <span>Payer {linkData?.final_amount !== undefined ? linkData.final_amount : linkData?.amount}€</span>
                </>
              )}
            </button>

            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 rounded-lg p-4">
                <p className="text-[#FF3B5C]">{error}</p>
              </div>
            )}
          </form>
        </motion.div>

        <p className="text-center text-[#6B6B6E] text-sm mt-6">
          En procédant au paiement, vous acceptez nos conditions générales de vente.
        </p>
      </div>
    </div>
  );
};

export default PaymentLink;
